import { Route } from '../models';

interface Coordinate {
    lat: number;
    lng: number;
}

function calculateDistance(coord1: Coordinate, coord2: Coordinate): number {
    const R = 6371; // Radio de la Tierra en km
    const lat1 = coord1.lat * Math.PI / 180;
    const lat2 = coord2.lat * Math.PI / 180;
    const dLat = lat2 - lat1;
    const dLon = (coord2.lng - coord1.lng) * Math.PI / 180;

    // Fórmula de Haversine mejorada para mayor precisión
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1) * Math.cos(lat2) *
              Math.sin(dLon/2) * Math.sin(dLon/2);
    
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    const distance = R * c;

    // Factor de corrección para tráfico y rutas reales (15% adicional)
    return distance * 1.15;
}

function nearestNeighbor(routes: Route[]): Route[] {
    if (routes.length <= 1) return routes;

    const result: Route[] = [routes[0]];
    const unvisited = routes.slice(1);
    let totalDistance = 0;

    while (unvisited.length > 0) {
        let bestDistance = Infinity;
        let bestIndex = -1;
        const lastPoint = result[result.length - 1].coordinate!;

        // Buscar el punto más cercano considerando la distancia acumulada
        for (let i = 0; i < unvisited.length; i++) {
            const currentCoordinate = unvisited[i].coordinate;
            if (currentCoordinate) {
                const distance = calculateDistance(lastPoint, currentCoordinate);
                // Penalizar rutas que se desvían demasiado del centro
                const centerDeviation = calculateDistance(
                    {lat: 9.9281, lng: -84.0907}, // Centro de referencia
                    currentCoordinate
                );
                const adjustedDistance = distance + (centerDeviation * 0.1); // Factor de peso para la desviación

                if (adjustedDistance < bestDistance) {
                    bestDistance = adjustedDistance;
                    bestIndex = i;
                }
            }
        }

        if (bestIndex !== -1) {
            result.push(unvisited[bestIndex]);
            totalDistance += bestDistance;
            unvisited.splice(bestIndex, 1);
        } else {
            result.push(unvisited.shift()!);
        }
    }

    return result;
}

function twoOptSwap(route: Route[], i: number, k: number): Route[] {
    const newRoute = route.slice(0, i);
    newRoute.push(...route.slice(i, k + 1).reverse());
    newRoute.push(...route.slice(k + 1));
    return newRoute;
}

function calculateTotalDistance(route: Route[]): number {
    let totalDistance = 0;
    for (let i = 0; i < route.length - 1; i++) {
        const coord1 = route[i].coordinate;
        const coord2 = route[i + 1].coordinate;
        if (coord1 && coord2) {
            totalDistance += calculateDistance(coord1, coord2);
        }
    }
    return totalDistance;
}

function twoOptImprovement(routes: Route[]): Route[] {
    let improved = true;
    let bestRoute = [...routes];
    let bestDistance = calculateTotalDistance(bestRoute);
    let iterations = 0;
    const maxIterations = 100; // Límite de iteraciones para evitar bucles infinitos

    while (improved && iterations < maxIterations) {
        improved = false;
        iterations++;
        
        for (let i = 1; i < routes.length - 2; i++) {
            for (let k = i + 1; k < routes.length - 1; k++) {
                const newRoute = twoOptSwap(bestRoute, i, k);
                const newDistance = calculateTotalDistance(newRoute);
                
                // Calcular el centro de gravedad de la nueva ruta
                const centerLat = newRoute.reduce((sum, r) => sum + (r.coordinate?.lat || 0), 0) / newRoute.length;
                const centerLng = newRoute.reduce((sum, r) => sum + (r.coordinate?.lng || 0), 0) / newRoute.length;
                
                // Penalizar rutas que se alejan demasiado del centro ideal
                const centerPenalty = calculateDistance(
                    {lat: 9.9281, lng: -84.0907},
                    {lat: centerLat, lng: centerLng}
                ) * 0.05; // Factor de penalización por desviación del centro
                
                const adjustedDistance = newDistance + centerPenalty;
                
                if (adjustedDistance < bestDistance) {
                    bestRoute = newRoute;
                    bestDistance = adjustedDistance;
                    improved = true;
                }
            }
        }
    }

    return bestRoute;
}

export const optimizeWaypoints = async (routes: Route[]): Promise<Route[]> => {
    // Asegúrate de que todas las rutas tienen coordenadas válidas
    const validRoutes = routes.filter(route => route.coordinate && route.coordinate.lat && route.coordinate.lng);
    
    if (validRoutes.length <= 1) return validRoutes;

    // Mantener el primer elemento fijo
    const firstRoute = validRoutes[0];
    const routesToOptimize = validRoutes.slice(1);

    // Aplicar Nearest Neighbor empezando desde el primer elemento
    let optimizedRoutes = nearestNeighbor([firstRoute, ...routesToOptimize]);
    
    // Aplicar mejora 2-opt, pero manteniendo el primer elemento en su lugar
    optimizedRoutes = [firstRoute, ...twoOptImprovement(optimizedRoutes.slice(1))];
  
    return optimizedRoutes;
};